import React, { useState, useRef, useEffect } from "react";
import { IconButton, Typography } from "@mhome/ui";
import CircularProgress from "@mui/material/CircularProgress";
import MicIcon from "@mui/icons-material/Mic";
import KeyboardIcon from "@mui/icons-material/Keyboard";
import StopIcon from "@mui/icons-material/Stop";
import { useRecoilValue } from "recoil";
import { wsReadyState } from "../global-state";
import { useWebSocket } from "../ws/websocket";
import { v4 as uuidv4 } from "uuid";

// Constants
const MIN_RECORDING_TIME = 500; // 500ms
const MAX_RECORDING_TIME = 60; // 60 seconds

// Utility function
const blobToBase64 = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

const formatDuration = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

// ModeButton component
const ModeButton = ({ children, onClick, disabled }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <IconButton
      onClick={onClick}
      disabled={disabled}
      style={{
        width: 32,
        height: 32,
        padding: 4,
        backgroundColor: hovered ? "hsl(var(--accent))" : "hsl(var(--muted))",
        transition: "all 0.3s ease",
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {children}
    </IconButton>
  );
};

// VoiceButton component
const VoiceButton = ({ $recording, children, style, ...props }) => {
  const [hovered, setHovered] = useState(false);
  const isRecording = $recording === "true";

  // Merge background color: use style.backgroundColor if provided, otherwise use default
  const defaultBackgroundColor = isRecording
    ? "#ff4444"
    : hovered
    ? "hsl(var(--accent))"
    : "hsl(var(--muted))";

  return (
    <IconButton
      {...props}
      style={{
        flex: 1,
        height: 88,
        minHeight: 88,
        borderRadius: "16px",
        color: isRecording ? "white" : "hsl(var(--muted-foreground))",
        transition: "all 0.3s ease",
        ...style,
        backgroundColor: style?.backgroundColor || defaultBackgroundColor,
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {children}
    </IconButton>
  );
};

// CompactVoiceButton component
const CompactVoiceButton = ({ $recording, children, style, ...props }) => {
  const [hovered, setHovered] = useState(false);
  const isRecording = $recording === "true";

  // Merge background color: use style.backgroundColor if provided, otherwise use default
  const defaultBackgroundColor = isRecording
    ? "#ff4444"
    : hovered
    ? "hsl(var(--accent))"
    : "hsl(var(--muted))";

  return (
    <IconButton
      {...props}
      style={{
        flex: 1,
        height: 56,
        minHeight: 56,
        borderRadius: "16px",
        color: isRecording ? "white" : "hsl(var(--muted-foreground))",
        transition: "all 0.3s ease",
        ...style,
        backgroundColor: style?.backgroundColor || defaultBackgroundColor,
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {children}
    </IconButton>
  );
};

const VoiceInputComponent = ({
  mode,
  onModeToggle,
  onVoiceResult,
  isInputDisabled = false,
  variant = "default", // "default" or "compact"
  prompt = null,
  showModeButton = true,
  isGenerating = false,
  onStop = null,
}) => {
  // Voice recording state
  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState("");
  const [startPosition, setStartPosition] = useState({ x: 0, y: 0 });
  const [isCancelled, setIsCancelled] = useState(false);

  // Voice recording refs
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingStartTimeRef = useRef(null);
  const recordingTimerRef = useRef(null);
  const uploadIdRef = useRef(null);
  const recentlyStoppedRef = useRef(false);

  const wsReady = useRecoilValue(wsReadyState);
  const { sendAndWaitWs, sendAndForgetWs } = useWebSocket();

  // Combined disabled state that includes websocket connectivity
  const isDisabled = isInputDisabled || !wsReady;

  // Effect to handle automatic stop when max time is reached
  useEffect(() => {
    if (recordingDuration >= MAX_RECORDING_TIME) {
      handleMaxTimeReached();
    }
  }, [recordingDuration]);

  useEffect(() => {
    setIsProcessing(false);
  }, [isGenerating]);

  const handleMaxTimeReached = async () => {
    setError(
      `Maximum recording time of ${MAX_RECORDING_TIME} seconds reached.`
    );
    await stopRecording();
  };

  const startRecording = async () => {
    try {
      setError("");
      setRecordingDuration(0);

      if (recentlyStoppedRef.current) {
        setError("Recording too short. Please hold longer.");
        return;
      }

      if (!wsReady) {
        setError("Not connected to server");
        return;
      }

      uploadIdRef.current = uuidv4();

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: 16000,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      if (recentlyStoppedRef.current) {
        setError("Recording too short. Please hold longer.");
        return;
      }

      mediaRecorderRef.current = new MediaRecorder(stream, {
        mimeType: "audio/webm;codecs=opus",
      });

      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = async (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);

          try {
            const dataUrl = await blobToBase64(event.data);
            const base64AudioChunk = dataUrl.split(",")[1];
            sendAndForgetWs(
              "/file/upload/chunk",
              `${uploadIdRef.current}|${base64AudioChunk}`
            );
          } catch (err) {
            console.error("Failed to send chunk:", err);
          }
        }
      };

      mediaRecorderRef.current.start(200);
      recordingStartTimeRef.current = Date.now();
      setIsRecording(true);

      recordingTimerRef.current = setInterval(() => {
        const currentDuration =
          (Date.now() - recordingStartTimeRef.current) / 1000;
        setRecordingDuration(currentDuration);
      }, 100);
    } catch (err) {
      setError(
        "Microphone access denied. Please allow microphone access and try again."
      );
    }
  };

  const stopRecording = async () => {
    if (!mediaRecorderRef.current || !isRecording) return null;

    clearInterval(recordingTimerRef.current);

    return new Promise((resolve) => {
      let finalChunkProcessed = false;

      mediaRecorderRef.current.ondataavailable = async (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);

          try {
            const dataUrl = await blobToBase64(event.data);
            const base64AudioChunk = dataUrl.split(",")[1];

            sendAndForgetWs(
              "/file/upload/chunk",
              `${uploadIdRef.current}|${base64AudioChunk}`
            );

            if (finalChunkProcessed) {
              resolve(true);
            }
          } catch (err) {
            console.error("Failed to send chunk:", err);
            if (finalChunkProcessed) {
              resolve(true);
            }
          }
        } else if (finalChunkProcessed) {
          resolve(true);
        }
      };

      mediaRecorderRef.current.onstop = () => {
        finalChunkProcessed = true;
        setTimeout(() => {
          if (finalChunkProcessed) {
            resolve(true);
          }
        }, 100);
      };

      mediaRecorderRef.current.stop();
      mediaRecorderRef.current.stream
        .getTracks()
        .forEach((track) => track.stop());
      setIsRecording(false);
    });
  };

  const getEventPosition = (event) => {
    if (event.touches && event.touches[0]) {
      return { x: event.touches[0].clientX, y: event.touches[0].clientY };
    }
    return { x: event.clientX, y: event.clientY };
  };

  const handleHoldStart = async (event) => {
    event.preventDefault();

    if (!isRecording && !isProcessing) {
      const position = getEventPosition(event);
      setStartPosition(position);
      setIsCancelled(false);
      await startRecording();
    }
  };

  const handleHoldMove = (event) => {
    if (isRecording) {
      const currentPos = getEventPosition(event);
      const deltaX = currentPos.x - startPosition.x;

      // If user swipes left more than 50px, mark as cancelled
      if (deltaX < -50) {
        setIsCancelled(true);
      } else {
        setIsCancelled(false);
      }
    }
  };

  const handleHoldEnd = async (event) => {
    event.preventDefault();

    if (isRecording) {
      const recordingDuration = Date.now() - recordingStartTimeRef.current;

      if (recordingDuration < MIN_RECORDING_TIME) {
        await stopRecording();
        setError("Recording too short. Please hold longer.");
        setIsCancelled(false);
        return;
      }

      // Check if user swiped left to cancel
      if (isCancelled) {
        await stopRecording();
        setError("Cancelled");
        setIsCancelled(false);
        return;
      }

      setIsProcessing(true);
      setError("");
      try {
        await stopRecording();

        if (recordingDuration / 1000 <= MAX_RECORDING_TIME) {
          try {
            await sendAndWaitWs(
              "/file/upload/complete",
              JSON.stringify({
                id: uploadIdRef.current,
                mimeType: "audio/webm",
              })
            );

            const transcriptResponse = await sendAndWaitWs(
              "/asr/transcript",
              JSON.stringify({
                id: uploadIdRef.current,
              })
            );

            if (transcriptResponse?.error) {
              setError("Failed to recognize voice input.");
              return;
            }

            const transcriptText = JSON.parse(
              transcriptResponse?.payload || ""
            );

            if (transcriptText?.text) {
              let instruction = transcriptText.text;
              if (prompt) {
                instruction = `${prompt.title}\n${instruction}`;
              }

              // Call the callback with the transcribed text
              if (onVoiceResult) {
                await onVoiceResult(instruction);
              }
            } else {
              setError("Failed to recognize voice input.");
            }
          } catch (e) {
            console.error("Failed to complete audio upload", e);
            setError("Failed to complete audio upload.");
          }
        }
      } catch (err) {
        console.error("Error processing audio:", err);
        setError("Failed to process audio. Please try again.");
      } finally {
        // TODO: temp hack
        // setIsProcessing(false);
        setIsCancelled(false);
      }
    } else {
      recentlyStoppedRef.current = true;
      setTimeout(() => {
        recentlyStoppedRef.current = false;
      }, 200);

      if (mediaRecorderRef.current) {
        await stopRecording();
      }
      setIsCancelled(false);
      // TODO: temp hack
      // setIsProcessing(false);
    }
  };

  const toggleMode = () => {
    if (onModeToggle) {
      onModeToggle();
    }
    setError("");
  };

  // Choose the appropriate voice button component based on variant
  const VoiceButtonComponent =
    variant === "compact" ? CompactVoiceButton : VoiceButton;

  if (mode === "text") {
    // Show only the mode toggle button when in text mode
    return showModeButton ? (
      <ModeButton onClick={toggleMode} disabled={isDisabled}>
        <MicIcon style={{ fontSize: "18px" }} />
      </ModeButton>
    ) : null;
  }

  // Voice mode UI
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "8px", flex: 1 }}>
      {showModeButton && (
        <ModeButton onClick={toggleMode} disabled={isDisabled}>
          <KeyboardIcon style={{ fontSize: "18px" }} />
        </ModeButton>
      )}

      <VoiceButtonComponent
        $recording={isRecording.toString()}
        onTouchStart={isGenerating ? undefined : handleHoldStart}
        onTouchMove={isGenerating ? undefined : handleHoldMove}
        onTouchEnd={isGenerating ? undefined : handleHoldEnd}
        onMouseDown={isGenerating ? undefined : handleHoldStart}
        onMouseMove={isGenerating ? undefined : handleHoldMove}
        onMouseUp={isGenerating ? undefined : handleHoldEnd}
        onMouseLeave={isGenerating ? undefined : handleHoldEnd}
        onClick={isGenerating ? onStop : undefined}
        disabled={isProcessing || isDisabled}
        style={{
          touchAction: "none",
          backgroundColor: isCancelled && isRecording ? "#666" : undefined,
        }}
      >
        {isGenerating ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <StopIcon style={{ fontSize: "20px" }} />
            <Typography
              variant="caption"
              style={{ fontSize: "10px", marginTop: "2px" }}
            >
              Stop
            </Typography>
          </div>
        ) : isProcessing ? (
          <CircularProgress size={20} color="inherit" />
        ) : isRecording ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Typography
              variant="body2"
              style={{ fontSize: "14px", fontWeight: 500 }}
            >
              {isCancelled
                ? "Release to cancel"
                : `Recording ${formatDuration(recordingDuration)}`}
            </Typography>
            {!isCancelled && (
              <Typography
                variant="caption"
                style={{ fontSize: "11px", opacity: 0.7 }}
              >
                Swipe left to cancel
              </Typography>
            )}
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Typography
              variant="body2"
              style={{ fontSize: "16px", fontWeight: 500 }}
            >
              Hold to talk
            </Typography>
            {error && (
              <Typography
                variant="caption"
                color="error"
                style={{ fontSize: "10px", marginTop: "4px" }}
              >
                {error}
              </Typography>
            )}
          </div>
        )}
      </VoiceButtonComponent>
    </div>
  );
};

export default VoiceInputComponent;
